import {Injectable, OnDestroy} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NotificationType} from '../enum/notification.enum';
import {JWTToken} from '../models/authentication';
import {AuthenticationService, DefaultNotificationService, StorageService} from '../services';
import {LoginTranslationsService} from 'src/app/shared/translations';

/**
 * Checks whether the user still has a valid token.
 */
@UntilDestroy()
@Injectable()
export class AuthChildGuard implements OnDestroy {
  private jwtToken: JWTToken;

  constructor(
    private router: Router,
    private authentication: AuthenticationService,
    private notificationService: DefaultNotificationService,
    private loginTranslationsService: LoginTranslationsService,
    private storage: StorageService
  ) {
    this.storage.castToken
      .pipe(untilDestroyed(this))
      .subscribe((token: JWTToken) => this.jwtToken = token);
  }

  public ngOnDestroy(): void {
  }

  /**
   * Checks whether the user still has a valid token.
   */
  public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (this.jwtToken !== null) {
      const user = this.jwtToken;
      const divideBy = 1000;
      const currentUnixTimestamp = Math.floor(+new Date() / divideBy);
      // check if the token is still valid
      if (user.expireAt > currentUnixTimestamp) {
        // logged in so return true
        return true;
      }
      // in case it is not valid, then tell user that session has expired
      this.notificationService.showNotification({
        type: NotificationType.WARNING,
        message: this.loginTranslationsService.i18nErrorSessionExpired
      });
      // log out the user since the token is not valid anymore
      this.authentication.logout();
      this.storage.redirectUrl = state.url;
      this.router.navigate(['/login']);
      return false;
    }
    this.notificationService.showNotification({
      type: NotificationType.WARNING,
      message: this.loginTranslationsService.i18nErrorNotLoggedIn
    });
    this.storage.redirectUrl = state.url;
    // not logged in so redirect to login page with the return url
    this.router.navigate(['/login']);
    return false;
  }
}
